/**
 * La Coochie product card (snippets/lc-product-card.liquid).
 * Colour swatch hover/click swaps the card image + price + size availability from the card's own JSON; "Add to bag"
 * opens the in-card size list; a size click arms the button; the second click adds via Shopify.actions.updateCart
 * (which emits shopify:cart:lines-update from the cart drawer scope) and opens the drawer. A sold-out size sends
 * the shopper to the product page (waitlist); a product without a size option adds straight away.
 * Never dispatches shopify:product:select (REVIEW-theme-dev §1.3).
 *
 * With the grid's "plus" button style the add button is a + icon on the image: its states go to aria-label, and the size
 * panel's own button (sheetAdd, the phone sheet's button) confirms the add on desktop too.
 * Card JSON: { colors: { "<colour>": { p, cp, img?, alt? } }, variants: [[id, colour, size, available]], hasColor, hasSize }
 * (the colour key is "" when the product has no colour option). Elements are reached through Horizon refs
 * (image, price, swatches[], sizes, sizeButtons[], add, sheetAdd, status, data); data-testids are for the tests only.
 */
import { Component } from '@theme/component';
import { isTouchDevice } from '@theme/utilities';

const WIDTHS = [320, 480, 640, 960];
const ERROR_MS = 3000;

/** @type {LcProductCard | null} the card whose size list is open (one at a time) */
let openCard = null;

document.addEventListener('pointerdown', (event) => {
  if (openCard && event.target instanceof Node && !openCard.contains(event.target)) openCard.closeSizes();
});
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && openCard) openCard.closeSizes();
});

/** @typedef {{ id: number, color: string | null, size: string | null, available: boolean }} CardVariant */

class LcProductCard extends Component {
  requiredRefs = ['add', 'data'];
  /** @type {{ colors: Record<string, { p: string, cp: string | null, img?: string, alt?: string }>, variants: CardVariant[], hasColor: boolean, hasSize: boolean }} */
  #data = { colors: {}, variants: [], hasColor: false, hasSize: false };
  /** @type {string | null} */ #color = null;
  /** @type {string | null} */ #size = null;
  #busy = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */ #errorTimer;

  connectedCallback() {
    super.connectedCallback();
    try {
      const raw = JSON.parse(this.refs.data.textContent || '{}');
      this.#data = {
        colors: raw.colors ?? {},
        variants: (raw.variants ?? []).map(([id, color, size, available, final]) => ({ id, color, size, available, final: final === 1 })),
        hasColor: Boolean(raw.hasColor),
        hasSize: Boolean(raw.hasSize),
      };
    } catch (error) {
      console.error('[lc-product-card] bad JSON', error);
    }
    this.#color = this.dataset.color || null;
    this.#syncSizes();
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener('pointerdown', this.#onPointerDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('focusout', this.#onFocusOut);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    clearTimeout(this.#pressTimer);
    if (openCard === this) openCard = null;
    clearTimeout(this.#errorTimer);
  }

  /** @returns {HTMLElement[]} */
  get #swatches() {
    return /** @type {HTMLElement[]} */ (this.refs.swatches ?? []);
  }

  /** @returns {HTMLElement[]} */
  get #sizeButtons() {
    return /** @type {HTMLElement[]} */ (this.refs.sizeButtons ?? []);
  }

  /**
   * Keyboard focus leaving the card closes the size list (WCAG 2.4.11: the open panel covers the title and swatches,
   * the mobile sheet covers the page). `relatedTarget` is null when focus goes to the body — WebKit does that on a
   * mouse click on a size button and every browser does on a window blur — so that case is left to the document
   * pointerdown listener above; focus is not moved.
   * @param {FocusEvent} event
   */
  #onFocusOut = (event) => {
    if (!this.#sizesOpen || this.#pressing) return;
    const next = event.relatedTarget;
    if (!(next instanceof Node) || this.contains(next)) return;
    this.closeSizes(false);
  };

  /**
   * iOS Safari does not focus a tapped <button>: a tap on a size moves focus from the first size (focused by openSizes) to the
   * nearest focusable ancestor — the cart drawer's <dialog> — so focusout saw focus leave the card and closed the list before the
   * tap's click arrived (2026-09-27, Caroline: "the shopping bag upsell buttons do not work"). A press that starts inside the
   * card is never a reason to close it; the flag outlives the tap's focusout → click sequence.
   */
  #pressing = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */ #pressTimer;
  #onPointerDown = () => {
    this.#pressing = true;
    clearTimeout(this.#pressTimer);
    this.#pressTimer = setTimeout(() => {
      this.#pressing = false;
    }, 600);
  };

  /* ---- swatches (Horizon's delegated `on:` events hand over the original event; target = the on: element) ---- */
  hoverSwatch(event) {
    if (isTouchDevice()) return;
    this.#selectColor(event.target);
  }

  pickSwatch(event) {
    this.#selectColor(event.target);
  }

  /** @param {EventTarget | null} node - the swatch button (Horizon hands over the `on:` element as target) */
  #selectColor(node) {
    const target = this.#swatches.find((swatch) => swatch === node || swatch.contains(/** @type {Node} */ (node)));
    if (!target) return;
    const color = target.dataset.color;
    if (!color || color === this.#color) return;
    this.#color = color;
    this.dataset.color = color;
    for (const swatch of this.#swatches) {
      const selected = String(swatch === target);
      swatch.setAttribute('data-selected', selected);
      swatch.setAttribute('aria-pressed', selected);
    }
    this.#swapImage();
    this.#updatePrice();
    if (this.refs.sheetColor instanceof HTMLElement) this.refs.sheetColor.textContent = color;
    this.#size = null;
    this.#syncSizes();
    this.#setState(this.#sizesOpen ? 'select' : 'idle');
  }

  get #colorData() {
    return this.#data.colors[this.#data.hasColor ? this.#color ?? '' : ''];
  }

  #variantsForColor() {
    return this.#data.variants.filter((v) => !this.#data.hasColor || v.color === this.#color);
  }

  #swapImage() {
    const img = this.refs.image;
    const data = this.#colorData;
    const url = data?.img;
    if (!(img instanceof HTMLImageElement) || !url) return;
    const base = url.replace(/([?&])width=\d+/, '$1width=960');
    const src = (w) => base.replace(/width=\d+/, `width=${w}`);
    // Only src/srcset/alt change: `sizes`, `loading` and `fetchpriority` stay as rendered.
    // Browsers keep painting the old image until the new source has decoded, so no preload dance is needed.
    img.srcset = WIDTHS.map((w) => `${src(w)} ${w}w`).join(', ');
    img.src = src(960);
    if (typeof data.alt === 'string') img.alt = data.alt;
    // the phone sheet's thumbnail (plus style) follows the colour too
    if (this.refs.sheetImage instanceof HTMLImageElement) {
      this.refs.sheetImage.srcset = `${src(120)} 120w, ${src(240)} 240w`;
      this.refs.sheetImage.src = src(240);
    }
  }

  #updatePrice() {
    const price = this.refs.price;
    const data = this.#colorData;
    if (!(price instanceof HTMLElement) || !data) return;
    if (this.refs.sheetPrice instanceof HTMLElement) this.refs.sheetPrice.textContent = data.p;
    price.innerHTML = data.cp
      ? `<span class="lc-card__price-now">${data.p}</span> <s class="lc-card__price-was">${data.cp}</s>`
      : `<span class="lc-card__price-now">${data.p}</span>`;
  }

  /* ---- sizes ---- */
  /** grid setting "Add to bag button" = plus icon on the image (data-add-style) */
  get #plus() {
    return this.dataset.addStyle === 'plus';
  }

  get #sizesOpen() {
    return this.refs.sizes instanceof HTMLElement && !this.refs.sizes.hidden;
  }

  #syncSizes() {
    const variants = this.#variantsForColor();
    for (const button of this.#sizeButtons) {
      const size = button.dataset.size;
      const available = variants.some((v) => v.size === size && v.available);
      const selected = String(size === this.#size);
      button.setAttribute('data-available', String(available));
      button.setAttribute('data-selected', selected);
      button.setAttribute('aria-pressed', selected);
      const soldOut = button.querySelector('[data-soldout]');
      if (soldOut instanceof HTMLElement) soldOut.hidden = available;
    }
  }

  openSizes() {
    if (openCard && openCard !== this) openCard.closeSizes();
    openCard = this;
    if (this.refs.sizes instanceof HTMLElement) this.refs.sizes.hidden = false;
    this.setAttribute('data-open', 'true');
    this.refs.add.setAttribute('aria-expanded', 'true');
    this.#setState('select');
    this.#sizeButtons[0]?.focus();
  }

  /** @param {boolean} [restoreFocus] - move focus back to the button when it was inside the list (Escape); false on focusout */
  closeSizes(restoreFocus = true) {
    if (openCard === this) openCard = null;
    const hadFocus =
      restoreFocus && this.refs.sizes instanceof HTMLElement && this.refs.sizes.contains(document.activeElement);
    if (this.refs.sizes instanceof HTMLElement) this.refs.sizes.hidden = true;
    this.removeAttribute('data-open');
    this.refs.add.setAttribute('aria-expanded', 'false');
    this.#size = null;
    this.#syncSizes();
    this.#setState('idle');
    if (hadFocus && this.refs.add instanceof HTMLElement) this.refs.add.focus();
  }

  pickSize(event) {
    const button = this.#sizeButtons.find((b) => b === event.target || b.contains(event.target));
    if (!button) return;
    this.#size = button.dataset.size ?? null;
    this.#syncSizes();
    const variant = this.#selectedVariant();
    if (!variant) return this.#setState('select');
    this.#setState(variant.available ? 'ready' : 'soldout');
  }

  #selectedVariant() {
    if (this.#data.hasSize && !this.#size) return null;
    return this.#variantsForColor().find((v) => !this.#data.hasSize || v.size === this.#size) ?? null;
  }

  /* ---- add ---- */
  /** @param {Event} [event] */
  async addClick(event) {
    if (this.#busy) return;
    const state = this.refs.add.dataset.state;
    if (state === 'idle' && this.#data.hasSize) return this.openSizes();
    // the + icon has no copy of its own: a second tap closes the list (the panel/sheet carries the Add to bag button)
    if (this.#plus && this.#sizesOpen && this.refs.add.contains(/** @type {Node} */ (event?.target ?? null))) return this.closeSizes();
    if (state === 'select' || state === 'error') return;
    const variant = this.#selectedVariant();
    if (!variant) return;
    if (state === 'soldout' || !variant.available) {
      window.location.href = `${this.dataset.productUrl}?variant=${variant.id}`;
      return;
    }
    await this.#add(variant.id);
  }

  /** Product in the final-sale collection (data-final-sale) or the variant's custom.final_sale metafield. */
  #finalSale(variant) {
    return this.dataset.finalSale === 'true' || Boolean(variant?.final);
  }

  /** @param {number} variantId */
  async #add(variantId) {
    const actions = window.Shopify?.actions;
    const variant = this.#data.variants.find((v) => v.id === variantId) ?? null;
    const attributes = this.#finalSale(variant) ? [{ key: 'Final Sale', value: 'Yes' }] : [];
    this.#busy = true;
    this.#setState('adding');
    try {
      if (actions?.updateCart) {
        const result = await actions.updateCart(
          { lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1, attributes }] },
          { event: { context: 'product' } }
        );
        if (result?.userErrors?.length) throw new Error(result.userErrors.map((e) => e.message).join(', '));
        await actions.openCart?.();
      } else {
        // Fallback when the standard-actions bundle is absent: plain form post, the cart page shows the result.
        const form = document.createElement('form');
        form.method = 'post';
        form.action = '/cart/add';
        form.innerHTML = `<input name="id" value="${variantId}"><input name="quantity" value="1">${
          attributes.length ? '<input name="properties[Final Sale]" value="Yes">' : ''
        }`;
        document.body.append(form);
        form.submit();
        return;
      }
      if (this.#data.hasSize) this.closeSizes();
      else this.#setState('idle');
    } catch (error) {
      console.error('[lc-product-card] add failed', error);
      this.#showError();
    } finally {
      this.#busy = false;
    }
  }

  /** Error copy on the button + live region for ~3 s, then back to the armed state. */
  #showError() {
    this.#setState('error');
    if (this.refs.status instanceof HTMLElement) this.refs.status.textContent = this.dataset.textError ?? '';
    clearTimeout(this.#errorTimer);
    this.#errorTimer = setTimeout(() => {
      if (this.refs.status instanceof HTMLElement) this.refs.status.textContent = '';
      if (this.refs.add.dataset.state === 'error') this.#setState(this.#data.hasSize ? 'ready' : 'idle');
    }, ERROR_MS);
  }

  /** @param {'idle' | 'select' | 'ready' | 'soldout' | 'adding' | 'error'} state */
  #setState(state) {
    const text = {
      idle: this.dataset.textAdd,
      select: this.dataset.textSelect,
      ready: this.#finalSale(this.#selectedVariant()) ? this.dataset.textFinal || this.dataset.textAdd : this.dataset.textAdd,
      soldout: this.dataset.textWaitlist,
      adding: this.dataset.textAdding,
      error: this.dataset.textError,
    }[state];
    for (const button of [this.refs.add, this.refs.sheetAdd]) {
      if (!(button instanceof HTMLElement)) continue;
      button.dataset.state = state;
      if (text == null) continue;
      if (this.#plus && button === this.refs.add) button.setAttribute('aria-label', text);
      else button.textContent = text;
    }
  }
}

if (!customElements.get('lc-product-card')) {
  customElements.define('lc-product-card', LcProductCard);
}
